/**
 * English is the source of truth for the UI translation keys. Every other
 * language provides a Partial of these keys and falls back here. Keys are flat
 * and namespaced by surface. `{name}` placeholders are filled by translate().
 *
 * Scope note: this covers the whole app UI chrome — nav, landing, create, the
 * generation popup, auth, catalog, the editor, admin, player, generation
 * status, novel detail, profile, legal, feedback, and the story-graph. Novel
 * *content* (titles, scene text, choices) stays in the story's own language.
 */
export const en = {
  "common.loadMore": "Load more",
  "maintenance.eyebrow": "Intermission",
  "maintenance.title": "Maintenance in progress",
  "maintenance.text": "We're updating Lorvok — it usually takes a couple of minutes. This page comes back by itself; no need to reload.",
  "maintenance.drafts": "Unsaved changes in the editor stay where they are.",
  "maintenance.checking": "Checking the connection…",
  "common.loadMoreFailed": "Could not load more stories. Please try again.",
  "editor.deleteCharacter": "Delete character",
  "editor.confirmDeleteCharacter": "Delete {name}? Unsaved, so Undo by leaving without saving.",
  "editor.deletedCharacter": "Deleted {name}.",
  "editor.storyTone": "Story tone",
  "editor.storyToneNote": "Guides the mood of future AI edits.",
  "editor.endingCanContinue": "Readers can continue the story past this ending",
  // ── Top navigation ──
  "nav.catalog": "Catalog",
  "nav.create": "Create",
  "nav.myNovels": "My Novels",
  "nav.admin": "Admin",
  "nav.signIn": "Sign in",
  "nav.signOut": "Sign out",
  "nav.viewGeneration": "View generation progress",
  "nav.genQueued": "Queued…",
  "nav.genRunning": "Generating… {progress}%",
  "nav.genCompleted": "Novel ready",
  "nav.genFailed": "Generation failed",
  "nav.interfaceLanguage": "Interface language",
  "nav.menu": "Menu",
  "nav.feedback": "Feedback",

  // ── Shared controls ──
  "common.createNovel": "Create Novel",
  "common.play": "Play",
  "common.playTitle": "Play {title}",
  "common.byAuthor": "{name}",
  "common.details": "Details",

  // ── Landing ──
  "landing.eyebrow": "Free interactive visual novels",
  "landing.heroTitle": "Play the story. Choose the ending.",
  "landing.heroLead": "Interactive stories you read in your browser. Make the choices, and your route decides which ending you reach. Free to read, no account needed — the stories and illustrations are created with AI.",
  "landing.playNow": "Play now",
  "landing.playNowTitle": "Play now: {title}",
  "landing.browseCatalog": "Browse the catalog",
  "landing.authorsPrompt": "Writing your own story?",
  "landing.authorsLink": "Create a novel",
  "landing.featuredLabel": "Featured novel",
  "landing.howEyebrow": "How it works",
  "landing.howHeading": "From the first scene to your ending",
  "landing.step1Title": "Pick a story",
  "landing.step1Text": "Mystery, romance, horror, fantasy and more. Open any novel and start from its first scene.",
  "landing.step2Title": "Make your choices",
  "landing.step2Text": "At key moments the story asks what you do. Each choice sends it down a different branch.",
  "landing.step3Title": "Reach your ending",
  "landing.step3Text": "Every route ends its own way. Play again and choose differently to find the other endings.",
  "landing.authorsNote": "For authors: describe an idea, and Lorvok drafts a branching novel with characters, scenes and endings that you can edit before you publish it.",
  "landing.builtEyebrow": "Built for stories",
  "landing.builtHeading": "A real visual novel, not a chat log",
  "landing.feat1Title": "Choices that matter",
  "landing.feat1Text":
    "Every choice sends the story down its own branch, and each route leads to one of several distinct endings.",
  "landing.feat2Title": "See every scene",
  "landing.feat2Text": "Each place has its own backdrop, and the characters step on stage as the story brings them in.",
  "landing.feat3Title": "No dead ends",
  "landing.feat3Text": "Every route is checked before a story is published, so whichever way you go, you reach a real ending.",
  "landing.feat4Title": "Read anywhere",
  "landing.feat4Text": "In the browser on your phone or computer, with no app to install. Your progress is saved on this device.",
  "landing.showcaseEyebrow": "From the catalog",
  "landing.showcaseHeading": "Stories you can play right now",
  "landing.browseAll": "Browse all",
  "landing.ctaHeading": "Ready for your first choice?",
  "landing.ctaText": "Reading is free and needs no account. Your progress is saved on this device, so you can come back to a story later.",

  // ── Page titles and descriptions (browser tab, search results; lib/pageMeta.ts) ──
  "meta.landingTitle": "Lorvok — Free Interactive Visual Novels",
  "meta.landingDescription": "Play free interactive visual novels in your browser. Make the choices, and your route decides the ending. Mystery, romance, horror, fantasy and more, no sign-up needed to read.",
  "meta.catalogTitle": "Catalog of free interactive visual novels",
  "meta.catalogDescription": "Browse free interactive visual novels: mystery, romance, horror, fantasy and more. Pick a story, make the choices and reach your own ending, right in your browser.",
  "meta.pricingDescription": "Lorvok credit packs and what each action costs. Reading is free; one-time purchases, no subscription.",
  "meta.termsDescription": "The terms that govern your use of Lorvok, the AI visual novel platform, including credits and purchases.",
  "meta.privacyDescription": "How Lorvok collects, uses and protects your personal data, and the rights you have over it.",
  "meta.refundDescription": "Lorvok's refund policy for credit purchases: unused credits are refundable within 14 days.",
  "meta.createTitle": "Create a visual novel",
  "meta.editorTitle": "Novel editor",
  "meta.novelDescription": "An interactive visual novel on Lorvok. Make the choices and reach your own ending.",
  "meta.authorTitle": "{name}: interactive stories",
  "meta.authorDescription": "Interactive visual novels by {name}. Read them free in your browser.",

  "continue.heading": "Continue reading",
  "continue.resume": "Continue",

  // ── Create ──
  "create.eyebrow": "Create",
  "create.h1Line1": "Create a visual novel",
  "create.h1Line2": "from one prompt",
  "create.subtitle": "Describe your story — characters, setting, mood. The AI handles the rest.",
  "create.aiIdea": "✨ Come up with an idea",
  "create.aiIdeaThinking": "Thinking…",
  "create.aiIdeaHint": "Type a word or a rough thought and AI turns it into an original premise — or leave the box empty for a surprise.",
  "create.aiIdeaFailed": "Couldn't come up with an idea. Try again.",
  "create.aiIdeaNoCredits": "Not enough credits — an idea costs {price}. Top up on the Pricing page.",
  "create.aiIdeaRejected": "This seed can't be used. Try different words.",
  "create.aiIdeaRateLimited": "Too many ideas in a row — wait a moment and try again.",
  "create.ideaLabel": "Idea",
  "create.ideaPlaceholder": "Describe your visual novel — setting, characters, mood…",
  "create.genre": "Genre",
  "create.tone": "Tone",
  "create.length": "Length",
  "create.language": "Language",
  "create.lengthShort": "Short",
  "create.lengthMedium": "Medium",
  "create.lengthLong": "Long",
  "create.scenesShort": "~12 scenes",
  "create.scenesMedium": "~15 scenes",
  "create.scenesLong": "~20 scenes",
  "create.styleNotes": "Style notes",
  "create.optional": "optional",
  "create.artStyle": "Art style",
  "create.artStylePlaceholder": "e.g. anime, watercolour, noir comic, pixel art",
  "create.artStyleHint": "One consistent look for every image. Leave blank for the default style.",
  "create.styleNotesPlaceholder":
    "Art style, mood references… e.g. 'anime painterly, rain-soaked neon streets'",
  "create.generateImages": "Generate images",
  "create.generateImagesDesc":
    "Render cover, backgrounds and character art. Turn off for a faster text-only draft you can illustrate later.",
  "create.emotions": "Character emotions",
  "create.emotionsDesc": "Up to 3 facial expressions for each character — they change as the scenes play out.",
  "create.emotionsPrice": "Adds {price}.",
  "create.emotionsNeedImages": "Needs images — turn on Generate images to add expressions.",
  "create.submit": "Generate Novel",
  "create.submitting": "Starting…",
  "create.costs": "Costs {price}",
  "create.costsWithBalance": "Costs {price} · Balance {balance}",
  "create.notEnoughWithBalance":
    "Not enough credits — generating costs {price}, you have {balance}.",
  "create.notEnough": "Not enough credits — generating costs {price}.",
  "create.providerNote": "{name} generation",
  "create.genre.mystery": "Mystery",
  "create.genre.romance": "Romance",
  "create.genre.fantasy": "Fantasy",
  "create.genre.sciFi": "Sci-Fi",
  "create.genre.horror": "Horror",
  "create.genre.thriller": "Thriller",
  "create.genre.adventure": "Adventure",
  "create.genre.drama": "Drama",
  "create.genre.comedy": "Comedy",
  "create.genre.historical": "Historical",
  "editor.mentionScene": "＋ Scene",
  "editor.mentionCharacter": "＋ Character",
  "editor.publishedBadge": "Published",
  "validation.too_many_scenes": "The novel has more scenes than allowed.",
  "validation.too_many_characters": "The novel has more characters than allowed.",
  "validation.too_many_endings": "The novel has more endings than allowed.",
  "validation.duplicate_scene_id": "Two scenes share the same ID.",
  "validation.empty_scene_text": "The scene has no text.",
  "validation.scene_text_too_long": "The scene text is over the length limit.",
  "validation.too_many_choices": "The scene has too many choices.",
  "validation.missing_start_scene": "The start scene doesn't exist.",
  "validation.missing_ending": "The novel needs at least one ending.",
  "validation.broken_ending_link": "An ending points to a scene that no longer exists.",
  "validation.broken_scene_link": "A choice leads to a scene that no longer exists.",
  "validation.unreachable_scene": "Players can't reach this scene from the start.",
  "validation.dead_end_scene": "The scene has no choices and isn't marked as an ending.",
  "validation.no_path_to_ending": "No path from this scene leads to an ending.",
  "validation.invalid_support_link": "The support link must be an http(s) link to a supported donation platform.",
  "genre.tag.supernatural": "Supernatural",
  "genre.tag.psychological": "Psychological",
  "genre.tag.gothic": "Gothic",
  "genre.tag.school": "School",
  "genre.tag.noir": "Noir",
  "genre.tag.detective": "Detective",
  "genre.tag.crime": "Crime",
  "genre.tag.paranormal": "Paranormal",
  "genre.tag.suspense": "Suspense",
  "genre.tag.action": "Action",
  "genre.tag.tragedy": "Tragedy",
  "genre.tag.dystopian": "Dystopian",
  "genre.tag.cyberpunk": "Cyberpunk",
  "genre.tag.post-apocalyptic": "Post-apocalyptic",
  "genre.tag.slice-of-life": "Slice of life",
  "genre.tag.survival": "Survival",
  "genre.tag.dark-fantasy": "Dark fantasy",
  "genre.tag.urban-fantasy": "Urban fantasy",
  "genre.tag.occult": "Occult",
  "genre.tag.folklore": "Folklore",
  "create.tone.mysterious": "Mysterious",
  "create.tone.hopeful": "Hopeful",
  "create.tone.dark": "Dark",
  "create.tone.heartwarming": "Heartwarming",
  "create.tone.tense": "Tense",
  "create.tone.melancholic": "Melancholic",
  "create.tone.whimsical": "Whimsical",
  "create.tone.bittersweet": "Bittersweet",
  "create.genreDesc.mystery": "Clues, secrets and a truth to uncover.",
  "create.genreDesc.romance": "Feelings and relationships at the heart of it.",
  "create.genreDesc.fantasy": "Magic, other worlds and their own rules.",
  "create.genreDesc.sciFi": "Future tech, space and bold what-ifs.",
  "create.genreDesc.horror": "Dread, fear and things that shouldn't be.",
  "create.genreDesc.thriller": "High stakes, danger and relentless tension.",
  "create.genreDesc.adventure": "A journey, discovery and bold risks.",
  "create.genreDesc.drama": "Emotional conflict and hard human choices.",
  "create.genreDesc.comedy": "Light, funny and quick on its feet.",
  "create.genreDesc.historical": "Set in a vivid, real past era.",
  "create.toneDesc.mysterious": "Hidden truths and unanswered questions.",
  "create.toneDesc.hopeful": "Light ahead, even through hardship.",
  "create.toneDesc.dark": "A bleak world, cruelty and moral weight.",
  "create.toneDesc.heartwarming": "Warmth, kindness and human closeness.",
  "create.toneDesc.tense": "Suspense and pressure — never quite safe.",
  "create.toneDesc.melancholic": "Quiet sadness and wistful beauty.",
  "create.toneDesc.whimsical": "Playful, quirky and lightly magical.",
  "create.toneDesc.bittersweet": "Joy and loss, tangled together.",

  // ── Generation-started popup ──
  "popup.eyebrow": "Generation started",
  "popup.title": "Your novel is being written",
  "popup.lead":
    "This usually takes about {estimate}. It keeps going in the background — leave this page freely and the button in the header will show you when it's ready.",
  "popup.whileYouWait": "While you wait, play a story",
  "popup.playNow": "Play now",
  "popup.watchProgress": "Watch progress",
  "popup.close": "Close",
  "popup.likesTitle": "{count} likes",

  // ── Generation time estimates (used by the popup) ──
  "estimate.shortImages": "10–15 minutes",
  "estimate.shortText": "5–10 minutes",
  "estimate.mediumImages": "15–25 minutes",
  "estimate.mediumText": "10–15 minutes",
  "estimate.longImages": "20–30 minutes",
  "estimate.longText": "15–20 minutes",

  // ── Catalog (chrome only; novel titles/descriptions stay in their own language) ──
  "catalog.eyebrow": "Public catalog",
  "catalog.heading": "Discover stories",
  "catalog.emptyTitle": "No public stories yet.",
  "catalog.emptyText": "Create and publish a novel to see it here.",
  "catalog.viewDetails": "View details for {title}",
  "catalog.filtersAria": "Filter stories",
  "catalog.filterLanguage": "Language",
  "catalog.filterAllLanguages": "All languages",
  "catalog.filterGenre": "Genre",
  "catalog.filterAllGenres": "All genres",
  "catalog.filterSelectedGenre": "Selected genre",
  "catalog.filterMoreGenres": "More genres",
  "catalog.filterFewerGenres": "Fewer genres",
  "catalog.filterEmptyTitle": "No stories match these filters.",
  "catalog.filterEmptyText": "Try another language or genre.",
  "catalog.filterClear": "Show all stories",
  "catalog.filterLoadFailed": "Couldn't load these stories. Please try again.",
  "catalog.filterRetry": "Try again",
  "catalog.cardLanguage": "Language: {language}",

  // ── Author profile ──
  "author.eyebrow": "Author",
  "author.subtitle": "{count} published novels",
  "author.ownSubtitle": "Your published novels ({count})",
  "author.empty": "No published novels yet.",
  "author.notFoundTitle": "Author not found",
  "author.notFoundText": "There is no author at this address. The link may be outdated or have a typo.",
  "author.notFoundCta": "Browse the catalog",
  "author.back": "← Back to catalog",
  "author.editProfile": "Edit profile",
  "author.memberSince": "on Lorvok since {date}",
  "author.sortLabel": "Sort",
  "author.sortNewest": "Newest first",
  "author.sortOldest": "Oldest first",
  "author.sortPopular": "Most popular",
  "catalog.sortRecommended": "Recommended",
  "author.statNovels": "Novels",
  "author.statViews": "Views",
  "author.statLikes": "Likes",
  "author.statFinished": "Finished",
  "author.statLanguages": "Languages",
  "author.nowShowing": "Now showing",
  "author.novelsHeading": "Author's novels",
  "library.tabsAria": "Novels view",
  "library.filterAria": "Filter by status",
  "library.filterAll": "All",
  "library.filterPublished": "Published",
  "library.filterDrafts": "Drafts",
  "library.filterEmpty": "No novels in this view.",
  "library.draftBadge": "Draft",

  // ── Auth ──
  "auth.signInTitle": "Sign in",
  "auth.signInSubtitle": "Lorvok is currently an invite-only {beta}.",
  "auth.betaLinkLabel": "beta",
  "auth.email": "Email",
  "auth.emailOrUsername": "Email or username",
  "auth.password": "Password",
  "auth.working": "Working…",
  "auth.signIn": "Sign in",
  "auth.forgot": "Forgot your password?",
  "auth.needAccess": "Need access? {request}.",
  "auth.requestAccessLabel": "Request an invite",
  "auth.resetTitle": "Reset password",
  "auth.resetSubtitle": "Enter your invite email and the owner will reset your password for you.",
  "auth.sending": "Sending…",
  "auth.requestReset": "Request reset",
  "auth.backToSignIn": "Back to sign in",
  "auth.requestSentTitle": "Request sent",
  "auth.requestSentInfo":
    "If that email is in the beta, the owner will reset your password and share new credentials with you. (Invite email is off during the beta, so this is handled by hand.)",
  "auth.failed": "Authentication failed.",
  "auth.couldNotSend": "Could not send your request.",

  // ── Not-in-beta screen ──
  "beta.notInTitle": "You're not in the beta yet",
  "beta.signOut": "Sign out",
  // ── editor ──
  "editor.addAppearanceBeforeRegen": "Add an appearance description before regenerating.",
  "editor.addChoice": "Add choice",
  "editor.addScene": "Add scene",
  "editor.addSceneBranchingTitle": "Add a scene branching from \"{title}\"",
  "editor.addToThisScene": "Add to this scene:",
  "editor.addedChoiceNoLongerEnding": "Added a choice — this scene is no longer an ending.",
  "editor.addedSceneBranching": "Added a scene branching from here.",
  "editor.addedSceneNoLongerEnding": "Added a scene. \"{title}\" is no longer an ending.",
  "editor.age": "Age",
  "editor.ageTitle": "The character's age in years.",
  "editor.aiBarNote": "Hi! I'm your AI story editor. Tell me what to change — for example “make the ending warmer” or “add a branch after a scene”. Point me at a scene or character with @: “rewrite @scene:\"…\" colder”. I'll ask if anything is unclear and show a plan before changing anything.",
  "editor.aiEdit": "AI Edit",
  "editor.aiEditConversation": "AI edit conversation",
  "editor.aiModeLabel": "AI edit mode",
  "editor.aiModeThorough": "Thorough",
  "editor.aiModeThoroughHint": "I'll ask when a request is unclear before changing anything.",
  "editor.aiModeFast": "Fast",
  "editor.aiModeFastHint": "I decide the details myself and propose a plan you approve or correct.",
  "editor.scrollToLatest": "Scroll to the latest message",
  "editor.newMessagesJump": "{count} new — jump to latest",
  "editor.regenerateProposal": "I'll regenerate the whole novel from its original idea.",
  "editor.regenerateLabel": "Regenerate the whole novel",
  "editor.regenerateWarning": "This runs a full generation from the original idea and creates a new novel — the current draft is left as it is.",
  "editor.regenerateWholeNovel": "Regenerate whole novel",
  "editor.regenerateStarting": "Starting…",
  "editor.regenerateConfirm": "Regenerate the whole novel from its original idea? This runs a full generation and creates a new draft; any unsaved changes to the current draft will be lost.",
  "editor.regenerateCancelled": "Regeneration cancelled.",
  "editor.regenerateFailed": "Couldn't start regeneration.",
  "editor.leaveUnsavedConfirm": "You have unsaved changes in the editor. Leave and discard them?",
  "create.requestFailed": "Generation request failed.",
  "editor.structuralLabel": "Structure — {count} {count|one:change|other:changes}",
  "editor.structuralAddSceneTarget": "Add branch from “{from}”",
  "editor.structuralEditSceneTarget": "Rewrite “{scene}”",
  "editor.structuralAddCharacterTarget": "Add character",
  "editor.structuralDeleteCharacterTarget": "Delete character",
  "editor.structuralEditCharacterTarget": "Rewrite “{name}”",
  "editor.structuralSceneCastTarget": "Set the cast of “{scene}”",
  "editor.structuralNoCast": "no characters",
  "editor.structuralRenameTarget": "Rename “{from}”",
  "editor.structuralSetStartTarget": "Make it the start scene",
  "editor.structuralEndingMetaTarget": "Ending details for “{scene}”",
  "editor.structuralNovelMetaTarget": "Novel details",
  "editor.mediaCover": "Regenerate cover",
  "editor.mediaBackground": "Regenerate background",
  "editor.mediaNewPrompt": "New image prompt:",
  "editor.mediaPortrait": "Regenerate portrait",
  "editor.mediaAll": "Regenerate all images ({count})",
  "editor.structuralAddChoiceTarget": "New choice from “{from}”",
  "editor.structuralRetargetTarget": "Retarget a choice in “{scene}”",
  "editor.structuralRemoveChoiceTarget": "Remove a choice from “{scene}”",
  "editor.structuralDeleteTarget": "Delete scene",
  "editor.structuralEndingTag": "new ending",
  "editor.structuralApply": "Apply changes",
  "editor.planWontDo": "Won't be done:",
  "editor.planCast": "cast: {names}",
  "editor.planLocation": "location: {name}",
  "editor.planRestageNote": "Who is on stage is rebuilt from the text of new and rewritten scenes; sounds, special frames and location changes stay with their sentences. A character added to a scene steps on stage where the text names them.",
  "editor.structuralEditChoiceTarget": "Reword a choice in “{scene}”",
  "editor.structuralCharacterFieldsTarget": "Change “{name}”",
  "editor.structuralRenameLocationTarget": "Rename a location",
  "editor.structuralSceneLocationTarget": "Location of “{scene}”",
  "editor.structuralRestageTarget": "Rebuild the storyboard of “{scene}” from its text",
  "editor.structuralStagingTarget": "Storyboard of “{scene}”",
  "editor.stagingFrame": "frame {n}",
  "editor.stagingFrames": "frames {from}–{to}",
  "editor.stagingNobody": "nobody on stage",
  "editor.planEndingFinal": "final — can't be continued",
  "editor.mediaSkippedMissing": "No picture for “{name}”: it was not created.",
  "editor.mediaSkippedPortrait": "The portrait of {name} was not repainted: the new description did not apply.",
  "editor.mediaArtStyle": "New art style “{notes}” — repaint all {count} images",
  "editor.aiEditLabel": "AI edit",
  "editor.allEndingsFinal": "All endings are marked final — nothing to continue.",
  "editor.appearance": "Appearance",
  "editor.appearanceHint": "Describes how they look — this is the portrait prompt. Click “Regenerate portrait” to apply.",
  "editor.appearanceTemplate": "A {age}-year-old {gender} who reads as {traits}.",
  "editor.appearanceTitle": "A description of how the character looks. This exact text is the prompt sent to the image model.",
  "editor.appearsInNoScenes": "Appears in no scenes",
  "editor.appearsInScenes": "Appears in {count} {count|one:scene|other:scenes} — highlighted in the outline and graph.",
  "editor.appliedChanges": "Applied {count} {count|one:change|other:changes}.",
  "editor.appliedReviewSave": "Applied. Review and Save, or Undo.",
  "editor.appliedWithSkipped": "Applied with {count} skipped. Review and Save, or Undo.",
  "editor.applying": "Applying…",
  "editor.applyingChanges": "Applying {count} {count|one:change|other:changes}…",
  "editor.applyingPlanFailed": "Applying the plan failed.",
  "editor.approveAndRun": "Approve & run",
  "editor.assets": "Assets",
  "editor.backToMyNovels": "Back to My Novels",
  // Editor redesign: top tabs, Unity-style inspector, Locations tab.
  "editor.tabGraph": "Graph",
  "editor.tabCharacters": "Characters",
  "editor.tabLocations": "Locations",
  "editor.closeInspector": "Close panel",
  "editor.location": "Location",
  "editor.locationName": "Location name",
  "editor.untitledLocation": "Untitled location",
  "editor.addLocation": "New location",
  "editor.noLocationsYet": "No locations yet",
  "editor.usedInScenes": "Scenes: {count}",
  "editor.createNewLocationOption": "＋ New location…",
  "editor.editInLocations": "Edit in Locations",
  "editor.regenerateLocation": "Regenerate image",
  "editor.imagePrompt": "Image prompt",
  "editor.deleteLocation": "Delete location",
  "editor.warnImagesHeading": "Images not generated",
  "editor.background": "Background",
  "editor.backgroundNoPrompt": "This background has no image prompt to regenerate from.",
  "editor.backgroundPrompt": "Background prompt",
  "editor.backgroundPromptTitle": "The description sent to the image model for this scene's background.",
  "editor.backgroundRegenFailed": "Background regeneration failed.",
  "editor.backgroundRegenerated": "Background regenerated. Review and Save to keep it.",
  "editor.backgroundRegeneratedForScene": "Background regenerated for this scene only — the other scenes keep the shared one. Review and Save.",
  "editor.cast": "Cast",
  "editor.characterAppearsHere": "{name} appears here",
  "editor.characterAppearsNoScenesTitle": "{name} appears in no scenes",
  "editor.characterLimitReached": "Character limit reached ({count}).",
  "editor.characterNoScenes": "Appears in no scenes. Add this character to a scene from the Scene tab.",
  "editor.charactersInScene": "Characters in scene",
  "editor.choiceRetargeted": "Choice retargeted to \"{title}\".",
  "editor.choiceTargetScene": "Choice target scene",
  "editor.choiceTo": "Choice to \"{title}\"",
  "editor.choiceToUnknown": "Choice to unknown scene ({id})",
  "editor.choices": "Choices",
  "editor.clearConversation": "Clear this conversation and start a new request",
  "editor.clickSceneToReference": "Click a scene or character to drop in a reference.",
  "editor.closeFullScreen": "Close full screen view",
  "editor.commaSeparated": "comma-separated",
  "editor.confirmDeleteNovel": "Delete \"{title}\"? This cannot be undone.",
  "editor.confirmDeleteScene": "Delete scene \"{title}\"? Unsaved, so Undo by leaving without saving.",
  "editor.confirmExtendUnsaved": "You have unsaved changes. Continuing the story leaves the editor and discards them. Continue anyway?",
  "editor.confirmPublishUnsaved": "You have unsaved changes. Publishing loads the last saved version and discards them. Publish anyway?",
  "editor.confirmRegenAll": "Regenerate all {count} images (cover, backgrounds, portraits)? Each one runs the image generator and may use image credits.",
  "editor.confirmRegenBackground": "Regenerate this scene's background? This generates a new image and may use image credits.",
  "editor.confirmRegenCover": "Regenerate the cover? This generates a new image and may use image credits.",
  "editor.confirmRegenPortrait": "Regenerate this portrait? This generates a new image and may use image credits.",
  "editor.confirmRevert": "Discard all unsaved changes and revert to the last saved version?",
  "editor.continuationCouldNotStart": "Continuation could not be started.",
  "editor.continueChoice": "Continue",
  "editor.continueFromEnding": "Continue from ending",
  "editor.continueStory": "Continue Story",
  "editor.continueStoryNote": "Grow this novel past one of its endings. New scenes are added to this draft.",
  "editor.costs": "Costs {amount}",
  "editor.couldNotTurnIntoChange": "I couldn't turn that into a change I'm able to make.",
  "editor.couldntDo": " Couldn't do: {items}.",
  "editor.couldntRegenAny": "Couldn't regenerate any images.",
  "editor.cover": "Cover",
  "editor.coverAlt": "{title} cover",
  "editor.coverNoPrompt": "This cover has no image prompt to regenerate from.",
  "editor.coverPrompt": "Cover prompt",
  "editor.coverPromptTitle": "The description sent to the image model for the cover.",
  "editor.coverRegenFailed": "Cover regeneration failed.",
  "editor.coverRegenerated": "Cover regenerated. Review and Save to keep it.",
  "editor.createCharacterTitle": "Create a new character with randomized name, gender, age and traits",
  "editor.dangerZone": "Danger Zone",
  "editor.deleteFailed": "Delete failed.",
  "editor.deleteNovel": "Delete Novel",
  "editor.deleteScene": "Delete scene",
  "editor.deletedScene": "Deleted \"{title}\".",
  "editor.describeBackground": "Describe this scene's background…",
  "editor.describeCharacterLooks": "Describe how this character looks…",
  "editor.describeCoverArt": "Describe the cover art…",
  "editor.describeWhatToChange": "Describe what you want changed.",
  "editor.description": "Description",
  "editor.discard": "Discard",
  "editor.done": "Done",
  "editor.editRegenBackground": "Edit, then click “Regenerate background” to apply.",
  "editor.editRegenCover": "Edit, then click “Regenerate cover” to apply.",
  "editor.editUndone": "Edit undone.",
  "editor.editorPanels": "Editor panels",
  "editor.editorUnavailable": "Editor unavailable",
  "editor.emotionVariantsKeep": "Emotions keep their current images — use Generate emotions below to redraw them to match.",
  "editor.emotions": "Emotions",
  "editor.emotionsHint": "Pick up to 3 expressions. The portrait is redrawn with them, so they all match.",
  "editor.emotionsReplaceHint": "Now: {emotions}. Generating replaces this set and redraws the portrait to match.",
  "editor.generateEmotions": "Generate emotions",
  "editor.generateEmotionsTitle": "Draw a new portrait plus the picked expressions from the Appearance description (uses credits)",
  "editor.saveFirstEmotionsTitle": "Save the novel first to generate this new character's emotions",
  "editor.confirmGenerateEmotions": "Generate {emotions} for this character for {price}? The portrait is redrawn too, and any other expressions it has are removed.",
  "editor.generatingEmotions": "Generating emotions…",
  "editor.emotionsGenerated": "Emotions generated. Review and Save to keep them.",
  "editor.emotionsPartlyGenerated": "{made} of {total} emotions generated — the rest show the neutral portrait. Review and Save to keep them.",
  "editor.emotionsUnsupported": "Emotions aren't available with the current image generator.",
  "editor.emotionsFailed": "Emotion generation failed.",
  "editor.endingBadge": "E",
  "editor.endingDescription": "Ending description",
  "editor.endingDetails": "Ending details",
  "editor.endingFallback": "Ending",
  "editor.endingFinalSuffix": " (final)",
  "editor.endingScene": "Ending scene",
  "editor.endingSceneNoChoices": "Ending scene — no choices.",
  "editor.endingTitle": "Ending title",
  "editor.eyebrowEditor": "Editor",
  "editor.fixValidationBeforePublish": "Fix validation errors before publishing",
  "editor.focusButton": "Focus",
  "editor.focusEditorLabel": "Focus editor — {title}",
  "editor.gender": "Gender",
  "editor.genderTitle": "The character's gender. Pick a chip or leave as-is.",
  "editor.guidance": "Guidance",
  "editor.guidancePlaceholder": "e.g. Follow the survivor into the city…",
  "editor.heresWhatIdChange": "Here's what I'd change.",
  "editor.imageGeneratorUnavailable": "Image generator unavailable — set a placeholder image. Review before saving.",
  "editor.imagesFailed": "{count} {count|one:image|other:images} failed to generate — showing placeholders. Regenerate to replace them.",
  "editor.instruction": "Instruction",
  "editor.instructionPlaceholder": "e.g. rewrite @scene:\"…\" - @scene:\"…\", I don't like the choice…",
  "editor.issuesBlockSaving": "{count} {count|one:issue blocks|other:issues block} saving",
  "editor.loadErrorApi": "Couldn't load this novel from the API. The editor needs a live server connection — check that the API is running and the novel still exists, then reload.",
  "editor.maxTraitsHint": "Max {count} traits — deselect one to add another.",
  "editor.moreIssues": "+{count} more…",
  "editor.moveSceneDown": "Move scene down in the list",
  "editor.moveSceneUp": "Move scene up in the list",
  "editor.music": "Music",
  "editor.assetStatusPending": "Not generated yet",
  "editor.assetStatusGenerating": "Generating",
  "editor.assetStatusReady": "Ready",
  "editor.assetStatusFailed": "Generation failed",
  "editor.assetStatusDeleted": "Removed",
  "editor.myNovels": "My Novels",
  "editor.name": "Name",
  "editor.nameTitle": "The character's name, shown to readers.",
  "editor.newCharacter": "New character",
  "editor.newCharacterCreated": "New character created — regenerate its portrait when ready.",
  "editor.newChoiceText": "New choice",
  "editor.newEndingTitle": "New ending",
  "editor.newRequest": "New request",
  "editor.newSceneText": "New scene — write what happens here.",
  "editor.newSceneTitle": "New scene",
  "editor.noCharactersInScene": "No characters in this scene yet.",
  "editor.noCharactersYet": "No characters yet — create one.",
  "editor.noChoicesYet": "No choices yet — add one, or mark this scene as an ending.",
  "editor.noImagesToRegen": "No images with prompts to regenerate yet.",
  "editor.noScenesMatch": "No scenes match “{query}”.",
  "editor.notEnoughCredits": "Not enough credits ({amount})",
  "editor.notReadyToPublish": "Not ready to publish — {count} {count|one:issue|other:issues}",
  "editor.nothingWasChanged": "Nothing was changed.",
  "editor.novelCouldNotLoad": "Novel could not be loaded.",
  "editor.novelCouldNotSave": "Novel could not be saved.",
  "editor.novelDetails": "Novel details",
  "editor.novelEditor": "Novel editor",
  "editor.novelNeedsOneEnding": "A novel needs at least one ending.",
  "editor.novelNeedsOneEndingMark": "A novel needs at least one ending. Mark another scene as an ending first.",
  "editor.novelNeedsOneScene": "A novel needs at least one scene.",
  "editor.onlyOwnerCanEdit": "Only novels owned by the current user can be edited.",
  "editor.openAltFullScreen": "Open {alt} full screen",
  "editor.openFocusEditor": "Open a distraction-free editor for this scene",
  "editor.openFullScreen": "Open full screen",
  "editor.openInCast": "Open in Cast (or add a reference while the AI prompt is focused)",
  "editor.openMyNovels": "Open My Novels",
  "editor.openMyNovelsToEdit": "Open My Novels to edit one of your stories.",
  "editor.openPlayer": "Open Player",
  "editor.openingEditor": "Opening editor…",
  "editor.optional": "optional",
  "editor.planChoiceTarget": "{title} — choice",
  "editor.planDiscarded": "Plan discarded.",
  "editor.planLabel": "Plan — {count} {count|one:change|other:changes}",
  "editor.planningFailed": "Planning failed.",
  "editor.portrait": "Portrait",
  "editor.portraitAlt": "{name} portrait",
  "editor.portraitRegenFailed": "Portrait regeneration failed.",
  "editor.portraitRegenerated": "Portrait regenerated. Review and Save to keep it.",
  "editor.portraitTitle": "The character's portrait, generated from the Appearance description below.",
  "editor.publish": "Publish",
  "editor.publishFailed": "Publish failed.",
  "editor.publishing": "Publishing…",
  "editor.randomPersonality": "Random personality",
  "editor.randomPersonalityTitle": "Roll random traits, gender and age for this character",
  "editor.rateLimitWaiting": "Rate limit reached — waiting {seconds}s, {done}/{total} done…",
  "editor.redoLabel": "Redo last change",
  "editor.redoTitle": "Redo (⌘/Ctrl+Shift+Z)",
  "editor.regenAllHint": "Repaints every image from its prompt. Paces around the rate limit and may take a minute; review and Save when done.",
  "editor.regenAllTitle": "Regenerate the cover, every scene background, and every character portrait from their prompts (uses image credits)",
  "editor.regenBackgroundTitle": "Generate a new background for this scene from the prompt above (uses image credits)",
  "editor.regenCoverTitle": "Generate a new cover from the prompt above (uses image credits)",
  "editor.regenPortraitTitle": "Generate a new portrait from the Appearance description (uses image credits)",
  "editor.regenerateAllImages": "Regenerate all images",
  "editor.artStyle": "Art style",
  "editor.artStyleTitle": "The visual style applied to every image",
  "editor.artStyleHint": "Change this, then regenerate all images to restyle the whole novel.",
  "editor.expandingArtStyle": "Preparing the art style…",
  "editor.artStyleFailed": "Couldn't prepare the art style.",
  "editor.regenerateBackground": "Regenerate background",
  "editor.regenerateCover": "Regenerate cover",
  "editor.regeneratePortrait": "Regenerate portrait",
  "editor.regeneratedImages": "Regenerated {done}/{total} {total|one:image|other:images}.",
  "editor.regenerating": "Regenerating…",
  "editor.regeneratingBackground": "Regenerating background…",
  "editor.regeneratingCover": "Regenerating cover…",
  "editor.regeneratingImagesProgress": "Regenerating images… {current}/{total} ({label})",
  "editor.regeneratingPortrait": "Regenerating portrait…",
  "editor.remove": "Remove",
  "editor.removeChoiceLabel": "Remove choice \"{text}\"",
  "editor.removeFromScene": "Remove {name} from scene",
  "editor.removedIncomingLinks": "Removed {count} incoming {count|one:link|other:links}.",
  "editor.reply": "Reply",
  "editor.replyPlaceholder": "Answer, or refine what you want…",
  "editor.revertTitle": "Discard all unsaved changes and return to the last saved version",
  "editor.revertToSaved": "Revert to saved",
  "editor.revertedToSaved": "Reverted to the last saved version.",
  "editor.reviewAndSaveKeep": "Review and Save to keep them.",
  "editor.save": "Save",
  "editor.saving": "Saving…",
  "editor.saveFirstBackgroundTitle": "Save the novel first to generate this new scene's background",
  "editor.saveFirstPortraitTitle": "Save the novel first to generate this new character's portrait",
  "editor.saveToGenBackground": "Save the novel to generate this scene's background.",
  "editor.saveToGenPortrait": "Save the novel to generate this character's portrait.",
  "editor.savedNote": "Saved.",
  "editor.scene": "Scene",
  "editor.sceneGraphIssue": "This scene has a graph issue — see the Save panel",
  "editor.sceneLimitReachedDot": "Scene limit reached ({count}).",
  "editor.sceneLimitReachedTitle": "Scene limit reached ({count})",
  "editor.sceneMaxChoices": "A scene can have at most {count} choices.",
  "editor.sceneOutline": "Scene outline",
  "editor.sceneText": "Scene text",
  "editor.sceneTitle": "Scene title",
  "editor.scenes": "Scenes",
  "editor.scenesBecameEnding": "{count} {count|one:scene|other:scenes} became an ending.",
  "editor.searchScenes": "Search scenes…",
  "editor.searchScenesLabel": "Search scenes",
  "editor.selectNovelToEdit": "Select a novel to edit",
  "editor.send": "Send",
  "editor.setAnotherStartFirst": "Set another scene as the start before deleting this one.",
  "editor.setAsStart": "Set as start",
  "editor.settings": "Settings",
  "editor.sharedWithOtherScenes": "Shared with {count} other {count|one:scene|other:scenes} — editing or regenerating gives this scene its own background.",
  "editor.shortSummaryPlaceholder": "Short summary shown in the catalog…",
  "editor.signInToEdit": "Sign in to edit your novels.",
  "editor.someFailed": "{count} failed — try again in a moment.",
  "editor.someFellBackPlaceholder": "{count} fell back to a placeholder (image generator unavailable).",
  "editor.startBadge": "S",
  "editor.startScene": "Start scene",
  "editor.startSceneUpdated": "Start scene updated.",
  "editor.starting": "Starting…",
  "editor.storyGraph": "Story graph",
  "editor.tags": "Tags",
  "editor.tagsPlaceholder": "mystery, drama, supernatural",
  "editor.taskLabelBackground": "background · {title}",
  "editor.taskLabelCover": "cover",
  "editor.taskLabelPortrait": "portrait · {name}",
  "editor.thinking": "Thinking…",
  "editor.title": "Title",
  "editor.tone": "Tone",
  "editor.toneBad": "bad",
  "editor.toneGood": "good",
  "editor.toneNeutral": "neutral",
  "editor.traits": "Traits",
  "editor.traitsTitle": "Personality traits, chosen from a fixed set. They shape how the character speaks and behaves when AI writes them.",
  "editor.undoEdit": "Undo edit",
  "editor.undoEditTitle": "Put the story back the way it was before the last applied plan",
  "editor.undoLabel": "Undo last change",
  "editor.undoTitle": "Undo (⌘/Ctrl+Z)",
  "editor.unknownSceneOption": "(unknown scene)",
  "editor.unsavedChanges": "Unsaved changes",
  "editor.unsavedChangesTitle": "You have unsaved changes",
  "editor.unsavedHint": "— ⌘/Ctrl+S to save",
  "editor.untitled": "Untitled",
  "editor.workingOutChange": "Working out what to change…",

  // ── admin ──
  "admin.added": "Added",
  "admin.adjustBalance": "Adjust balance ($)",
  "admin.apply": "Apply",
  "admin.applying": "Applying…",
  "admin.balanceAdjusted": "{verb} {amount} — new balance {balance}.",
  "admin.budgetOk": "OK",
  "admin.budgetPaused": "PAUSED — daily budget reached",
  "admin.charged": "Charged",
  "admin.close": "Close",
  "admin.colBalance": "Balance",
  "admin.colCount": "Count",
  "admin.colEmail": "Email",
  "admin.colFrom": "From",
  "admin.colGenerations": "Generations",
  "admin.colGranted": "Granted",
  "admin.colKind": "Kind",
  "admin.colLastActive": "Last active",
  "admin.colLastSeen": "Last seen",
  "admin.colMargin": "Margin",
  "admin.colMessage": "Message",
  "admin.colOperation": "Operation",
  "admin.colPrompt": "Prompt",
  "admin.colSpent": "Spent",
  "admin.colSrc": "Src",
  "admin.colStatus": "Status",
  "admin.colTitle": "Title",
  "admin.colWhen": "When",
  "admin.colWhere": "Where",
  "admin.deducted": "Deducted",
  "admin.done": "done",
  "admin.errorsHeading": "Errors",
  "admin.failedLoadActivity": "Failed to load activity.",
  "admin.failedLoadBudget": "Failed to load budget.",
  "admin.failedLoadErrors": "Failed to load errors.",
  "admin.failedLoadFeedback": "Failed to load feedback.",
  "admin.failedLoadMargin": "Failed to load margin.",
  "admin.failedUpdateBalance": "Failed to update the balance.",
  "admin.failedUpdateError": "Failed to update the error.",
  "admin.failedUpdateFeedback": "Failed to update feedback.",
  "admin.feedbackHeading": "Feedback & requests",
  "admin.filterIdea": "Ideas",
  "admin.filterBug": "Bugs",
  "admin.filterOther": "Other",
  "admin.filterArchive": "Archive",
  "admin.noFeedbackFilter": "Nothing here.",
  "admin.ignore": "Ignore",
  "admin.loading": "Loading…",
  "admin.tabMargin": "Margin",
  "admin.marginByPeriod": "Margin by period",
  "admin.marginByOperation": "Margin by operation · all time",
  "admin.marginOpCount": "Operations: {count}",
  "admin.periodOpsAria": "Operations in {period}",
  "admin.periodOpsEmpty": "No operations in this period.",
  "admin.periodOpenHint": "Click a period to see what each operation made.",
  "admin.periodDays": "Days",
  "admin.periodWeeks": "Weeks",
  "admin.periodMonths": "Months",
  "admin.periodHintDays": "Last 30 days, newest first · UTC",
  "admin.periodHintWeeks": "Last 12 weeks (Mon–Sun), newest first · UTC",
  "admin.periodHintMonths": "Last 12 months, newest first · UTC",
  "admin.colPeriod": "Period",
  "admin.colMarginPct": "Margin %",
  "admin.periodEmpty": "No paid operations yet.",
  "admin.marginHide": "Hide",
  "admin.marginHideAria": "Hide {period} from the report",
  "admin.marginHiddenToggle": "Hidden days ({count})",
  "admin.marginHiddenHint": "Hidden days are left out of every figure in the margin report. Nothing is deleted — restore a day to count it again.",
  "admin.marginRestore": "Restore",
  "admin.marginRestoreAria": "Restore {period} to the report",
  "admin.marginRestoreAll": "Restore all",
  "admin.marginExcludesHidden": "Hidden days left out of these figures: {count}.",
  "admin.marginReset": "Clear margin data",
  "admin.marginResetConfirm": "Clear the margin data? The report will count only operations from now on. Nothing is deleted — you can bring the data back.",
  "admin.marginResetSince": "Counting since {date}. Cleared before that: {count} operations, margin {margin}.",
  "admin.marginResetUndo": "Bring the data back",
  "admin.failedUpdateMargin": "Failed to update the margin report.",
  "admin.netMargin": "Net margin",
  "admin.noChangeBalance": "No change — balance stays {balance}.",
  "admin.noErrors": "No captured errors.",
  "admin.noFeedback": "No feedback or reset requests yet.",
  "admin.noNovels": "No novels yet.",
  "admin.noPaidOperations": "No paid operations yet.",
  "admin.novelsHeading": "Novels",
  "admin.play": "Play",
  "admin.prLink": "PR",
  "admin.realCost": "Real cost",
  "admin.resolve": "Resolve",
  "admin.searchAria": "Search users",
  "admin.searchPlaceholder": "Search users by email or username…",
  "admin.feedbackSearchPlaceholder": "Search by email or message…",
  "admin.spendByOperationHeading": "Spend by operation",
  "admin.suspendedBadge": "suspended",
  "admin.usersHeading": "Users",
  "admin.noUsers": "No users found.",
  "admin.openUserActivity": "Open user's activity",
  "admin.userAccessHeading": "Access",
  "admin.suspendAccount": "Suspend account",
  "admin.unsuspendAccount": "Lift suspension",
  "admin.confirmSuspend": "Suspend {email}? The account won't be able to use the site until you lift the suspension.",
  "admin.accountSuspendedNote": "This account is suspended and can't use the site.",
  "admin.failedUpdateAccount": "Failed to update the account.",
  "admin.title": "Admin",
  "admin.openFeedback": "Open feedback",
  "admin.feedbackDetailTitle": "Feedback",
  "admin.feedbackContact": "Reply contact",
  "admin.feedbackPage": "Page",
  "admin.feedbackContext": "Context",
  "admin.feedbackScreenshot": "Screenshot",
  "admin.feedbackScreenshotOpen": "Open screenshot",
  "admin.noScreenshot": "No screenshot attached",
  "admin.tabBilling": "Billing",
  "admin.tabUsers": "Users",
  "admin.colUsername": "Username",
  "admin.colRegistered": "Registered",
  "admin.colLastSignIn": "Last sign-in",
  "admin.usersCount": "{count} users",
  "admin.tabFeedback": "Feedback",
  "admin.tabErrors": "Errors",
  "admin.tabAnimations": "Animations",
  "admin.tabStyles": "Styles",
  "admin.providersHeading": "Providers",
  "admin.providersHint": "No API exposes these balances — record what each console shows so you know when to top up.",
  "admin.topUp": "Top up",
  "admin.notRecorded": "Not recorded",
  "admin.balanceUpdated": "Updated {date}",
  "admin.balancePlaceholder": "Balance, $",
  "admin.balanceAria": "Current {name} balance in dollars",
  "admin.noteOptional": "Note (optional)",
  "admin.save": "Save",
  "admin.failedLoadProviderBilling": "Could not load provider balances.",
  "admin.markupHeading": "Markup",
  "admin.markupHint": "A flat surcharge added on top of every operation's estimated cost — your commission. Set 0 to disable.",
  "admin.markupLabel": "Per-operation markup",
  "admin.markupPlaceholder": "Markup, $",
  "admin.failedLoadMarkup": "Could not load the markup.",
  "admin.failedSaveMarkup": "Could not save the markup.",
  "admin.todaysSpend": "Today's spend",
  "admin.topupAria": "Amount in dollars to add or deduct",
  "admin.topupPlaceholder": "e.g. 5 (use -5 to deduct)",

  // ── player ──
  "player.endingFallback": "Ending",
  "player.endingActions": "What next",
  "player.toNovel": "To the novel",
  "player.restart": "Restart",
  "player.linkCopied": "Link copied",
  "player.readAnother": "Read another story",
  "player.readAnotherTitle": "Next: {title}",
  "player.createOwn": "Create your own",
  "player.loadingStory": "Loading story…",
  "player.openEditor": "Edit",
  "player.exit": "Leave the story",
  "player.musicOff": "♪ Off",
  "player.musicOn": "♪ On",
  "player.muteMusic": "Mute music",
  "player.sourceApi": "API",
  "player.sourceCached": "Cached",
  "player.sourceFallback": "Fallback",
  "player.unmuteMusic": "Unmute music",

  // ── alive generation animation (stage captions) ──
  "alive.done": "Ready to read",
  "live.seeding": "Dreaming up your story…",
  "live.failed": "Generation stopped",
  "live.announce": "{stage} — {characters} characters, {scenes} scenes so far",

  // ── showcase (animation gallery) ──
  "showcase.back": "← Back",
  "showcase.title": "Watch your story come alive",

  // ── status ──
  "status.cancelFailed": "Cancel failed.",
  "status.cancelGeneration": "Cancel Generation",
  "status.cancelling": "Cancelling…",
  "status.downloadFailed": "Download failed. Please try again.",
  "status.downloadJson": "Download JSON",
  "status.editDraft": "Edit Draft",
  "status.eyebrow": "Generation",
  "status.generationCancelled": "Generation was cancelled.",
  "status.jobLabel": "Job: {id}",
  "status.noJobStarted": "No generation job has been started.",
  "status.novelSavedAsDraft": "Novel saved as draft.",
  "status.preview": "Preview",
  "status.publishFailed": "Publish failed.",
  "status.publishNow": "Publish Now",
  "status.publishing": "Publishing…",
  "status.retryFailed": "Retry failed.",
  "status.retryGeneration": "Retry Generation",
  "status.retrying": "Retrying…",
  "status.statusLabel": "Status: {status}",
  "status.stepBuildPlayableNovel": "Rendering art & assembling",
  "status.stepCharacterGeneration": "Creating characters",
  "status.stepPromptExpansion": "Expanding your idea",
  "status.stepSceneWriting": "Writing scenes",
  "status.stepSemanticNarrativeReview": "Reviewing the narrative",
  "status.stepStoryBibleGeneration": "Writing the story bible",
  "status.stepStoryGraphGeneration": "Building the story graph",
  "status.stepsTitle": "Steps",
  "status.title": "Generation status",
  "status.elapsed": "Elapsed",
  "status.totalTime": "Total time",

  // ── detail ──
  "detail.back": "← Back",
  "detail.characters": "Characters",
  "detail.endings": "Endings",
  "detail.eyebrow": "Novel",
  "detail.ownerLabel": "Your novel",
  "detail.like": "Like",
  "detail.liked": "Liked",
  "detail.likesLabel": "{count} likes",
  "detail.likesTitle": "{count} likes",
  "detail.loading": "Loading…",
  "detail.notLoadedBody": "It may have been removed, or the API is offline.",
  "detail.notLoadedTitle": "This novel could not be loaded",
  "detail.play": "Play",
  "detail.scenes": "Scenes",
  "detail.share": "Share",
  "detail.shareCopied": "Link copied to clipboard",
  "detail.viewsLabel": "{count} views",
  "detail.viewsTitle": "{count} views",
  "detail.audience": "Audience",
  "detail.writtenBy": "Written by",
  "detail.synopsis": "Synopsis",
  "detail.runtime": "Runtime",
  "detail.runtimeValue": "~{count} min",
  "detail.branches": "Branches",
  "detail.languages": "Languages",
  "detail.language": "Language",
  "detail.waysItEnds": "{count} ways it can end",
  "detail.endingGood": "Triumphant",
  "detail.endingBad": "Tragic",
  "detail.endingNeutral": "Ambiguous",
  "detail.endingsAria": "{good} triumphant, {bad} tragic, {neutral} ambiguous endings",
  "detail.storyMap": "Story map",
  "detail.branchMapAria": "Branching map: {scenes} scenes, {endings} endings",
  "detail.starring": "Starring",
  "detail.prologue": "Prologue",
  "detail.moreFromAuthor": "More from this author",

  // ── profile ──
  "profile.createNovel": "Create Novel",
  "profile.downloadFailed": "Download failed. Please try again.",
  "profile.downloadJson": "Download JSON",
  "profile.edit": "Edit",
  "profile.emptyHint": "Create your first visual novel from a prompt.",
  "profile.emptyMessage": "No novels yet.",
  "profile.eyebrow": "My Novels",
  "profile.heading": "My Novels",
  "profile.offlineFallback": "offline fallback",
  "profile.playAria": "Play {title}",
  "profile.publish": "Publish",
  "profile.publishFailed": "Publish failed.",
  "profile.publishing": "Publishing…",
  "profile.sourcePrefix": "Source: ",
  "profile.authorNameLabel": "Author name",
  "profile.authorNamePlaceholder": "Your author name",
  "profile.authorNameHint": "Shown as the byline on your published novels.",
  "profile.saveProfile": "Save",
  "profile.savingProfile": "Saving…",
  "profile.nameSaved": "Saved",
  "profile.nameSaveFailed": "Could not save your name.",
  "username.label": "Username",
  "username.placeholder": "e.g. NightWriter",
  "username.hint": "3–24 English letters and digits. It can't be changed later: it becomes your profile link. Your display name can be changed any time.",
  "username.invalid": "Use 3–24 English letters (A–Z) and digits, no spaces or symbols.",
  "username.reserved": "This username is reserved. Pick another one.",
  "username.rejected": "This username isn't allowed. Pick another one.",
  "username.taken": "This username is already taken.",
  "username.checking": "Checking availability…",
  "username.available": "Username is available.",
  "username.checkFailed": "Couldn't check the username. Try again.",
  "profile.usernamePermanent": "Username @{username} is permanent: your profile link never changes.",
  "profile.nameRequired": "Enter a name.",
  "profile.viewPublicProfile": "View public profile",
  "profile.bioLabel": "Bio",
  "profile.bioPlaceholder": "A few words about you",
  "profile.supportLinkLabel": "Support link",
  "profile.supportLinkHint": "Optional. Shown as a “Support the author” button on your author page — Boosty, Ko-fi, DonationAlerts, etc.",
  "profile.supportLinkInvalid": "Use an https:// link to a supported donation platform (Boosty, Ko-fi, DonationAlerts, Patreon, Buy Me a Coffee…), or leave it empty.",
  "author.supportAuthor": "Support the author",
  "profile.uploadAvatar": "Upload photo",
  "profile.removeAvatar": "Remove",
  "profile.avatarInvalidType": "Choose an image file.",
  "profile.avatarTooLarge": "That image is too large (max 2MB).",
  "profile.avatarUploadFailed": "Could not upload your avatar.",
  "profile.generateAvatar": "Generate avatar",
  "profile.avatarPromptPlaceholder": "Describe your avatar…",
  "profile.avatarGenerating": "Generating…",
  "profile.avatarGenerateFailed": "Could not generate your avatar.",
  "profile.avatarPaidHint": "Generating an avatar uses credits.",

  // ── comments ──
  "comments.title": "Comments",
  "comments.titleCount": "Comments ({count})",
  "comments.placeholder": "Share your thoughts…",
  "comments.replyPlaceholder": "Write a reply…",
  "comments.post": "Post comment",
  "comments.postReply": "Reply",
  "comments.posting": "Posting…",
  "comments.reply": "Reply",
  "comments.cancel": "Cancel",
  "comments.delete": "Delete",
  "comments.confirmDelete": "Delete this comment?",
  "comments.deleted": "[comment removed]",
  "comments.empty": "No comments yet. Be the first to comment.",
  "comments.loading": "Loading comments…",
  "comments.error": "Something went wrong. Please try again.",
  "comments.signInToComment": "Sign in to join the conversation.",
  "comments.signUpToComment": "Sign up or sign in to comment",
  "comments.likeAria": "Like",
  "comments.sortLabel": "Sort comments",
  "comments.sortByDate": "By date",
  "comments.sortByLikes": "By likes",
  "comments.rateLimited": "You're commenting too fast — please wait a moment.",
  "comments.deleteAllFrom": "Delete all from {name}",
  "comments.deleteAllFromTitle": "Remove every comment from this user",
  "comments.confirmPurge": "Delete all comments from {name}?",
  "comments.deleteTitle": "Delete comment?",
  "comments.optDeleteAll": "Delete all comments from this user",
  "comments.optBlock": "Block this user from commenting",
  "comments.block": "Block",
  "comments.blockTitle": "Block this user from commenting on your novels",
  "comments.confirmBlock": "Block {name} and remove their comments?",
  "comments.blocked": "You've been blocked from commenting on this author's novels.",
  "comments.loadMore": "Show more comments",
  "blocked.heading": "Blocked users ({count})",
  "blocked.unblock": "Unblock",

  // ── notifications ──
  "notifications.aria": "Notifications",
  "notifications.title": "Notifications",
  "notifications.empty": "No notifications yet.",
  "notifications.markAllRead": "Mark all read",
  "notifications.aNovel": "your novel",
  "notifications.comment": "{name} commented on {title}",
  "notifications.reply": "{name} replied to your comment on {title}",
  "notifications.like": "{name} liked {title}",

  // ── legal ──
  "legal.back": "← Back",
  "legal.toHome": "Back to home",

  // ── feedback ──
  "feedback.attachScreenshot": "Attach a screenshot of this page",
  "feedback.cancel": "Cancel",
  "feedback.attachImage": "Attach a screenshot",
  "feedback.pasteHint": "…or paste from the clipboard (Ctrl+V)",
  "feedback.removeImage": "Remove",
  "feedback.imageTooLarge": "Couldn't attach the image (too large or unsupported).",
  "feedback.imageTooMany": "You can attach at most {max} images.",
  "feedback.contactPlaceholder": "Discord username for a reply (optional)",
  "feedback.discordCta": "Community on Discord",
  "feedback.discordSub": "News, discussions and answers from the team",
  "feedback.kindLegend": "Feedback type",
  "feedback.close": "Close",
  "feedback.closeButton": "Close",
  "feedback.dialogLabel": "Send feedback",
  "feedback.genericError": "Something went wrong.",
  "feedback.kindBug": "Bug",
  "feedback.kindIdea": "Idea",
  "feedback.kindOther": "Other",
  "feedback.messagePlaceholder": "What happened, or what would you like to see?",
  "feedback.send": "Send",
  "feedback.sending": "Sending…",
  "feedback.subtitle": "Found a bug or have an idea? Tell us — it goes straight to the team.",
  "feedback.thanksSub": "Your note reached the team. We read every one.",
  "feedback.thanksTitle": "Thanks!",
  "feedback.title": "Send feedback",

  // ── graph ──
  "graph.choiceCount": "{count} choices",
  "graph.endBadge": "End",
  "graph.fitStoryToView": "Fit story to view",
  "graph.fitToView": "Fit to view",
  "graph.oneChoice": "1 choice",
  "graph.retargetHandle": "Drag onto another scene to retarget “{text}”",
  "graph.startBadge": "Start",
  "graph.zoomIn": "Zoom in",
  "graph.zoomOut": "Zoom out",

  // ── error ──
  "error.crashBody": "An unexpected error stopped this page. It has been reported. Try reloading, or head back home.",
  "error.crashTitle": "Something broke",
  "error.goHomeButton": "Go home",
  "error.reloadButton": "Reload",
  "editor.backgroundAlt": "{title} background",
  // ── Novel content localization ──
  "common.localized": "Localized",
  "common.localizedIn": "Also available in: {languages}",
  "create.localize": "Also localize into other languages",
  "create.localizeDesc": "Machine-translate this novel into the languages you pick so readers can play it in their own. Machine translation — review it later in the editor.",
  "player.readingLanguage": "Reading language",
  "player.machineTranslated": "Machine translation — it may contain errors.",
  "editor.localizeTitle": "Localize",
  "editor.localizeNote": "Machine-translate the whole novel so readers can play it in their language. Machine translation — review and edit the results afterwards.",
  "editor.localizeAvailable": "Already available in: {languages}",
  "editor.localizeButton": "Translate selected",
  "editor.localizeDone": "Translation added and saved.",
  "editor.localizeFailed": "Translation failed.",
  "editor.localizeReload": "Translation added and saved, but the editor could not reload it. Reload the page to see it.",
  "editor.warnings": "Warnings",
  "editor.warningsEmpty": "No warnings. When you change a character's voice — traits, gender, age, or name — the scenes that need rewriting show up here.",
  "editor.warningRegenTitle": "{name}: scenes out of date",
  "editor.warningRegenBody": "Changed {fields}. The written scenes still use the old version.",
  "editor.warningRegenScenes": "Scenes to rewrite: {count}",
  "editor.fixThis": "Fix",
  "editor.fixAll": "Fix all",
  "editor.fixing": "Rewriting scenes…",
  "editor.fixDone": "Scenes rewritten: {count}.",
  "editor.fixFailed": "Some scenes could not be rewritten.",
  "editor.regenNeededEyebrow": "Character changed",
  "editor.regenNeededTitle": "Rewrite the affected scenes?",
  "editor.regenNeededBody": "You changed {name}'s voice ({fields}). The scenes they appear in were written for the old version — rewrite them to match?",
  "editor.regenNeededScenes": "Scenes to rewrite: {count}",
  "editor.regenNeededSaveNote": "Your unsaved changes are saved first so the rewrite uses the new version.",
  "editor.regenerateNow": "Regenerate now",
  "editor.regenerateLater": "Later",
  "nav.legal": "Terms & privacy",
  "footer.label": "Site footer",
  "footer.terms": "Terms of Service",
  "footer.privacy": "Privacy Policy",
  "footer.refund": "Refund Policy",
  "footer.contact": "Contact",
  "footer.copyright": "© {year} {entity}",
  "legal.effective": "Effective {date}",
  "legal.otherDocs": "Related documents",
  "auth.signInSubtitleOpen": "Sign in to create, edit and publish your novels.",
  "auth.signUpTitle": "Create your account",
  "auth.signUp": "Create account",
  "auth.passwordHint": "At least {count} characters.",
  "auth.checkEmailTitle": "Confirm your email",
  "auth.checkEmailInfo": "We sent a link to activate your account. Open it, then sign in.",
  "auth.resetSubtitleOpen": "Enter your account email and we'll send you a link to set a new password.",
  "auth.resetSentOpen": "If an account exists for this email, a reset link is on its way. Open it to set a new password.",
  "auth.newPasswordTitle": "Set a new password",
  "auth.savePassword": "Save password",
  "auth.tabsAria": "Sign in or sign up",
  "auth.tabSignUp": "Sign up",
  "auth.signInTitleOpen": "Welcome back",
  "auth.resetSentTitleOpen": "Check your inbox",
  "auth.checkSpamHint": "No email after a couple of minutes? Check your spam folder.",
  "auth.resend": "Resend email",
  "auth.resendIn": "Resend in {time}",
  "auth.resentNote": "Email sent again.",
  "auth.changeEmail": "Use a different email",
  "beta.suspendedTitle": "This account is suspended",
  "footer.pricing": "Pricing",
  "footer.catalog": "Catalog",
  "footer.discord": "Join our Discord",
  "pricing.eyebrow": "Pricing",
  "pricing.thanks": "Thank you! Your payment went through — the credits will appear on your balance within a minute.",
  "pricing.loadFailed": "Could not load prices. Please try again in a moment.",
  "pricing.checkoutFailed": "Could not open the checkout. Please try again.",
  "pricing.regionUnavailable": "Purchases are not available in your country. Lorvok is not offered in the United Kingdom or the Russian Federation.",
  "pricing.paymentUnavailable": "Payments are temporarily unavailable. Please try again in a few minutes.",
  "pricing.worth": "≈ {count} medium {count|one:novel|other:novels}",
  "pricing.opening": "Opening checkout…",
  "pricing.buy": "Buy",
  "pricing.unavailable": "Purchases are temporarily unavailable. Please check back soon or contact support.",
  "pricing.costsHeading": "What things cost",
  "pricing.op.generateShort": "New short novel, ~12 scenes (text + images)",
  "pricing.op.generate": "New medium novel, ~15 scenes (text + images)",
  "pricing.op.generateLong": "New long novel, ~20 scenes (text + images)",
  "pricing.withoutImages": "without images — {price}",
  "pricing.op.extend": "Continue a story past an ending",
  "pricing.op.plan": "AI edit — plan a change",
  "pricing.op.apply": "AI edit — rewrite a scene or character",
  "pricing.op.image": "Regenerate a cover or background",
  "pricing.op.portrait": "Regenerate a character portrait",
  "pricing.op.novelEmotions": "Character emotions for a new novel (up to 3 per character)",
  "pricing.op.characterEmotions": "Emotions for one character (up to 3)",
  "pricing.op.translate": "Translate a novel (per language)",
  "pricing.op.avatar": "Generate a profile avatar",
  "pricing.op.idea": "Come up with a story idea",
  "pricing.failedRefunded": "If a generation or edit fails on our side, its credits are returned to your balance automatically.",
  "pricing.termNoSubscription": "One-time purchase — no subscription, no automatic renewal. Credits never expire.",
  "pricing.termRefund": "Unused credits can be refunded within 14 days of purchase.",
  "report.action": "Report",
  "report.novelAction": "Report this novel",
  "report.profileAction": "Report this profile",
  "report.title.novel": "Report this novel",
  "report.title.comment": "Report this comment",
  "report.title.profile": "Report this profile",
  "report.reasonLegend": "What's wrong?",
  "report.reason.sexual": "Sexually explicit content",
  "report.reason.minors": "Involves minors inappropriately",
  "report.reason.violence": "Promotes real violence or self-harm",
  "report.reason.hate": "Hate or harassment",
  "report.reason.realPerson": "Targets a real person",
  "report.reason.copyright": "Copies my work / infringes my rights",
  "report.reason.spam": "Spam or scam",
  "report.reason.other": "Something else",
  "report.details": "Details (optional)",
  "report.contact": "Your email, if we may reply (optional)",
  "report.submit": "Send report",
  "report.sending": "Sending…",
  "report.cancel": "Cancel",
  "report.pickReason": "Choose what's wrong first.",
  "report.failed": "Could not send the report. Please try again.",
  "common.tooManyRequests": "Too many attempts. Please try again later.",
  "report.rateLimited": "Too many reports from your network today. Please email {email} to send this one.",
  "report.sent": "Thank you — we'll review this report and remove anything that breaks our rules.",
  "admin.filterReports": "Reports",
  "admin.unpublishNovel": "Unpublish novel",
  "admin.removeComment": "Remove comment",
  "admin.takedownDone": "Done — the content is no longer public.",
  "admin.takedownFailed": "Could not take the content down.",
  "account.deleteHeading": "Delete account",
  "account.deleteOpen": "Delete my account…",
  "account.deleteTypeEmail": "Type {email} to confirm",
  "account.deleteConfirm": "Delete forever",
  "account.deleting": "Deleting…",
  "account.deleteFailed": "Could not delete the account. Please try again or contact support.",
  "cookies.label": "Cookie consent",
  "cookies.text": "We'd like to use analytics cookies (PostHog) to see how Lorvok is used and improve it. They stay off unless you accept — without them we only count visits anonymously, with nothing stored in your browser. Sign-in and settings storage is always on.",
  "cookies.accept": "Accept analytics",
  "cookies.decline": "Decline",
  "cookies.textShort": "No analytics cookies unless you accept.",
  "cookies.details": "Details",
  "cookies.acceptShort": "Accept",
  "cookies.settings": "Cookie settings",
  "credits.one": "{count} credit",
  "credits.few": "{count} credits",
  "credits.many": "{count} credits",
  "credits.other": "{count} credits",
  "nav.balanceAction": "Balance: {amount}. Buy more credits",
  "auth.acceptDocs": "I accept the {terms} and the {privacy}",
  "auth.termsLink": "Terms of Service",
  "auth.privacyLink": "Privacy Policy",
  "auth.docsRequired": "To create an account, accept the Terms of Service and the Privacy Policy.",
  "auth.passwordConfirm": "Repeat password",
  "auth.passwordMismatch": "The passwords don't match.",
  "auth.showPassword": "Show password",
  "auth.hidePassword": "Hide password",
  "beta.signedInAs": "Signed in as",
  "beta.contactSupport": "If you think this is a mistake, write to support:",
  "beta.suspendedBody": "Access to Lorvok is closed for this account.",
  "beta.notInBody": "This account isn't part of the closed beta yet. Ask the owner to add your email to the invite list.",
  "auth.newPasswordSubtitle": "Choose a new password for your Lorvok account.",
  "credits.compactK": "{n}k",
  "credits.compactM": "{n}M",
  "pricing.balanceLabel": "Your balance",
  "pricing.tabPacks": "Credit packs",
  "pricing.recommended": "Recommended",
  "pricing.factOneTime": "One-time purchase, no subscription",
  "pricing.factNoExpiry": "Credits never expire",
  "pricing.factRefund": "Unused credits refundable for 14 days",
  "pricing.termsToggle": "Payment terms",
  "pricing.op.read": "Read and play novels",
  "pricing.free": "Free",
  "pricing.heading": "Pay only for what you create",
  "pricing.lead": "Reading is free. Credits are spent only on creating and editing novels.",
  "pricing.bonus": "+{percent}% bonus",
  "pricing.tierStarter": "Starter",
  "pricing.tierCreator": "Creator",
  "pricing.tierStudio": "Studio",
  "pricing.enoughFor": "Enough for",
  "pricing.worthImages": "≈ {count} {count|one:image|other:images}",
  "pricing.worthEdits": "≈ {count} {count|one:AI scene edit|other:AI scene edits}",
  "pricing.per100": "{price} per 100 credits",
  "pricing.oneTime": "one-time payment",
  "footer.groupService": "Service",
  "footer.groupLegal": "Legal",
  "report.groupHarmful": "Harmful content",
  "report.groupOther": "Rights, spam and other",
  "account.deleteIrreversible": "This cannot be undone. We will permanently delete:",
  "account.deleteItemNovels": "your account and all your novels, including published ones",
  "account.deleteItemComments": "your comments and avatar",
  "account.deleteItemCredits": "any credits left on your balance",
  "account.deleteRefundNote": "Bought credits in the last 14 days? To get a refund for the unused ones, contact support before deleting.",
  "auth.signUpSubtitle": "An account lets you create and publish novels. You can read without one.",
  "auth.readerSignUpSubtitle": "Create a free account to like stories and comment.",
  "auth.readerSignInSubtitle": "Sign in to like stories and comment.",
  "pricing.consentTerms": "I am 18 or older and I accept the {terms} and the {refund}.",
  "pricing.refundLink": "Refund Policy",
  "pricing.consentStart": "I ask Lorvok to start providing the service immediately. I understand that if I withdraw within 14 days, I am refunded only for the credits I have not used, and that once all credits in the pack are used I lose my right of withdrawal.",
  "pricing.consentRequired": "Tick both boxes to continue.",
  "checkout.title": "Checkout",
  "checkout.summary": "Your order",
  "checkout.packTitle": "“{name}” credit pack",
  "checkout.paymentLink": "Lorvok credits",
  "checkout.listPrice": "Price",
  "checkout.taxHint": "VAT or sales tax for your country is calculated in the payment form.",
  "checkout.subtotal": "Subtotal",
  "checkout.tax": "Tax",
  "checkout.total": "Total",
  "checkout.loading": "Loading the payment form…",
  "checkout.success": "Payment received — credits arrive within a minute.",
  "checkout.done": "Done",
  "checkout.mor": "Payment is processed by Paddle.com, our Merchant of Record. Card details never reach Lorvok.",
  "report.name": "Your name (optional)",
  "report.goodFaith": "I believe in good faith that the information in this report is accurate and complete.",
  "report.goodFaithRequired": "Please confirm the report is made in good faith.",
  "ai.badge": "Created with AI",
  "ai.badgeShort": "AI",
  "ai.badgeTitle": "Story and illustrations generated with AI",
  "ai.siteNotice": "All stories and illustrations on Lorvok are created with AI.",
  "auth.ageConfirm": "I am 18 or older",
  "auth.ageRequired": "Please confirm you are 18 or older.",
  "auth.or": "or",
  "auth.continueWithGoogle": "Continue with Google",
  "auth.continueWithDiscord": "Continue with Discord",
  "auth.oauthNeedsConsent": "Tick both boxes above first.",
  "auth.oauthFailed": "Signing in with Google or Discord didn't work. Try again, or use your email.",
  "finishSignUp.title": "Finish signing up",
  "finishSignUp.subtitle": "One last step: choose the username people will know you by.",
  "finishSignUp.submit": "Continue",
  "finishSignUp.docsRequired": "To continue, accept the Terms of Service and the Privacy Policy.",
  "finishSignUp.failed": "Couldn't save your username. Try again.",
  "footer.age": "For adults 18+",
  "pricing.termAge": "Lorvok is for adults aged 18 and over.",
  "pricing.termMor": "Our order process is conducted by our online reseller Dodo Payments. Dodo Payments is the Merchant of Record for all our orders: it processes payments, issues invoices and handles customer service inquiries about payments and returns.",
  "pricing.termCredits": "Credits pay only for generating and editing novels with AI. They cannot be transferred, withdrawn, exchanged for money or used to buy anything else.",
  "pricing.termTax": "Prices are in US dollars. Depending on your country, VAT or sales tax is included in the price or added at checkout; the checkout shows the final amount before you pay.",
  "legal.translationNotice": "This is a translation. If it differs from the English version, the English version prevails, except where the law of your country requires otherwise.",
  "legal.showEnglish": "Read the English version",
  "legal.showTranslation": "Back to the translation",
  "legal.englishOnly": "This document is published in English, which is its governing version.",
  "timeline.open": "Timeline",
  "timeline.inspectorHint": "{count} {count|one:frame|other:frames}. Each frame is one reading beat with up to two characters on stage.",
  "timeline.title": "Timeline — {title}",
  "timeline.noText": "This scene has no text yet — write it first, then stage it frame by frame.",
  "timeline.summary": "{count} {count|one:frame|other:frames}",
  "timeline.restage": "Re-stage from text",
  "timeline.confirmRestage": "Replace this scene's staging with an automatic one based on who the text names? Manual casting, expressions, locations, sounds and special frames of this scene are removed.",
  "timeline.prev": "Previous",
  "timeline.next": "Next",
  "timeline.toChoices": "To the choices",
  "timeline.hiddenCuesNote": "This frame has a sound or a special frame. Those layers will return to the editor soon; readers still get them.",
  "timeline.removeHiddenCues": "Remove from this frame",
  "timeline.prevScene": "Previous scene",
  "timeline.prevScenePick": "Back to which scene?",
  "timeline.startSceneNote": "This is the start scene — nothing leads here.",
  "timeline.choicesTitle": "Choices at the end of the scene",
  "timeline.choicesHint": "Pick a choice to open the storyboard of the scene it leads to.",
  "timeline.endingNote": "This is an ending — no scene follows.",
  "timeline.frameN": "Frame {n} of {count}",
  "timeline.expressionAuto": "Auto ({emotion})",
  "timeline.removeFromFrame": "Take {name} off stage",
  "timeline.swapSides": "Swap sides",
  "timeline.stageFull": "The stage is full — take someone off first.",
  "timeline.notInScene": "new to scene",
  "timeline.location": "Location",
  "timeline.sceneLocation": "{name} (scene location)",
  "timeline.locationForward": "Also change the following frames in the same place",
  "timeline.layers": "Layers",
  "timeline.layersHint": "Sounds play and special frames show when the reader reaches this frame.",
  "timeline.cueSound": "Sound",
  "timeline.cueFrame": "Special frame",
  "timeline.cueFrameHint": "Replaces the location and characters for this frame.",
  "timeline.volume": "Volume",
  "timeline.loop": "Loop while on this frame",
  "timeline.removeCue": "Remove layer",
  "timeline.noSoundAssets": "This novel has no sound files yet.",
  "timeline.track.text": "Text",
  "timeline.track.location": "Location",
  "timeline.track.sound": "Sound",
  "timeline.track.frame": "Special",
  "timeline.asset.cover": "Cover",
  "timeline.asset.portrait": "Portrait",
  "timeline.asset.background": "Location",
  "timeline.asset.music": "Music",
  "timeline.asset.ending_image": "Ending image",
  "timeline.asset.sound": "Sound",
  "timeline.asset.illustration": "Illustration",
  "timeline.asset.video": "Video",
  "emotion.neutral": "Neutral",
  "emotion.happy": "Happy",
  "emotion.sad": "Sad",
  "emotion.angry": "Angry",
  "emotion.afraid": "Afraid",
  "emotion.surprised": "Surprised",
  "emotion.tired": "Tired",
  "emotion.worried": "Worried",
  "emotion.embarrassed": "Embarrassed",
  "emotion.disgusted": "Disgusted",
  "emotion.disappointed": "Disappointed",
  "emotion.relieved": "Relieved",
  "emotion.confused": "Confused",
  "validation.invalid_section": "A frame of this scene's timeline is broken (a missing character, location or file). Open the timeline to fix it.",
  "validation.public_projection": "This is the readers' copy of the novel, not the one you can edit. Reload the page while signed in as its author.",
  "timeline.undo": "Undo",
  "timeline.redo": "Redo",
  "timeline.menu": "More actions",
  "timeline.close": "Close",
  "timeline.slotEmpty": "Add a character",
  "timeline.nobodyToAdd": "Everyone is already on stage.",
  "timeline.pickEmotion": "Expression of {name}",
  "timeline.noEmotionSprites": "This character has only the base portrait so far — regenerate it on the Cast tab to get expressions.",
  "timeline.removeLane": "Take {name} out of the scene",
  "timeline.addLane": "Add a character to the scene",
  "timeline.laneCell": "{name} on stage in frame {n}",
  "timeline.newFramePlaceholder": "Frame text…",
  "timeline.scriptHint": "Enter splits a frame at the cursor, Backspace at the start of a frame joins it to the previous one. Drag down a character's lane to set the frames they are on stage — at most two per frame.",
  "timeline.viewLabel": "Timeline view",
  "timeline.viewFrame": "Frame",
  "timeline.viewList": "List",
  "timeline.whoOnStage": "On stage",
  "timeline.whoOnStageMany": "On stage in {count} frames",
  "timeline.whoOnStageHint": "Up to {count} per frame — tap to put a character on stage or take them off.",
  "timeline.inSomeFrames": "in {count} of {total}",
  "timeline.stageFullShort": "stage full",
  "timeline.locationMany": "Location for {count} frames",
  "timeline.phoneFrameHint": "Swipe the stage to move between frames. Enter splits the frame at the cursor.",
  "timeline.phoneListHint": "Tap a frame to open it. Tap numbers to select several frames and change them together.",
  "timeline.pickFrame": "Select frame {n}",
  "timeline.pickedCount": "{count} selected",
  "timeline.deleteFrames": "Delete",
  "timeline.confirmDeleteFrames": "Delete the selected frames ({count}) together with their text?",
  "timeline.clearPick": "Clear selection",
  "player.tutorialForward": "Tap here to read on",
  "player.tutorialBack": "Tap here to go back",
  "player.tutorialSkip": "Skip",
} as const;
